import React, { useEffect, useState } from 'react';
import { getAccounts } from '../api/accounts';
import { getTransactionsForAccount } from '../api/transactions';
import { Account, Transaction, Owner } from '../types/models';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress, Alert, Typography, IconButton, Collapse, Box, Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import EditIcon from '@mui/icons-material/Edit';
import Drawer from '@mui/material/Drawer';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { getAccount, updateAccount, getAccountCounterparties } from '../api/accounts';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { getOwners } from '../api/owners';

const AccountsPage: React.FC = () => {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [txsByAccount, setTxsByAccount] = useState<Record<string, Transaction[]>>({});
  const [txsLoading, setTxsLoading] = useState<Record<string, boolean>>({});
  const [txsError, setTxsError] = useState<Record<string, string | null>>({});
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerAddress, setDrawerAddress] = useState<string | null>(null);
  const [drawerAccount, setDrawerAccount] = useState<Account | null>(null);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [drawerError, setDrawerError] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editRelevant, setEditRelevant] = useState<boolean | null>(null);
  const [editIgnored, setEditIgnored] = useState<boolean | null>(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [expandedCounterparties, setExpandedCounterparties] = useState<string | null>(null);
  const [counterpartiesByAccount, setCounterpartiesByAccount] = useState<Record<string, Account[]>>({});
  const [counterpartiesLoadingByAccount, setCounterpartiesLoadingByAccount] = useState<Record<string, boolean>>({});
  const [counterpartiesErrorByAccount, setCounterpartiesErrorByAccount] = useState<Record<string, string | null>>({});
  const [showTransactionsFor, setShowTransactionsFor] = useState<string | null>(null);
  const [owners, setOwners] = useState<Owner[]>([]);

  useEffect(() => {
    getAccounts()
      .then(setAccounts)
      .catch((err) => setError(err.message || 'Failed to load accounts'))
      .finally(() => setLoading(false));
    getOwners().then(setOwners);
  }, []);

  const ownerDict = Object.fromEntries(owners.map(o => [o.id, o]));

  const handleExpandClick = (address: string) => {
    if (expanded === address) {
      setExpanded(null);
      return;
    }
    setExpanded(address);
    if (!txsByAccount[address]) {
      setTxsLoading((prev) => ({ ...prev, [address]: true }));
      getTransactionsForAccount(address)
        .then((txs) => {
          setTxsByAccount((prev) => ({ ...prev, [address]: txs }));
          setTxsError((prev) => ({ ...prev, [address]: null }));
        })
        .catch((err) => {
          setTxsError((prev) => ({ ...prev, [address]: err.message || 'Failed to load transactions' }));
        })
        .finally(() => {
          setTxsLoading((prev) => ({ ...prev, [address]: false }));
        });
    }
  };

  const handleEditClick = (address: string) => {
    setDrawerOpen(true);
    setDrawerAddress(address);
    setDrawerAccount(null);
    setDrawerLoading(true);
    setDrawerError(null);
    setEditError(null);
    getAccount(address)
      .then((acc) => {
        setDrawerAccount(acc);
        setEditName(acc.name || '');
        setEditRelevant(acc.relevant);
        setEditIgnored(acc.ignored_as_target);
      })
      .catch((err) => setDrawerError(err.message || 'Failed to load account'))
      .finally(() => {
        setDrawerLoading(false);
      });
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setDrawerAddress(null);
    setDrawerAccount(null);
    setDrawerLoading(false);
    setDrawerError(null);
    setEditError(null);
  };

  const handleEditSave = async () => {
    if (!drawerAccount) return;
    setEditLoading(true);
    setEditError(null);
    try {
      const updated = await updateAccount(drawerAccount.address, {
        name: editName,
        relevant: editRelevant,
        ignored_as_target: editIgnored,
      });
      setDrawerAccount(updated);
      setAccounts((prev) => prev.map((a) => (a.address === updated.address ? updated : a)));
      setDrawerOpen(false);
    } catch (err: any) {
      setEditError(err.message || 'Failed to update account');
    } finally {
      setEditLoading(false);
    }
  };

  if (loading) return <CircularProgress />;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <TableContainer component={Paper}>
      <Typography variant="h5" sx={{ m: 2 }}>Accounts</Typography>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell />
            <TableCell>Address</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Owner</TableCell>
            <TableCell>Current Balance</TableCell>
            <TableCell>Relevant</TableCell>
            <TableCell>Created At</TableCell>
            <TableCell>Edit</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {accounts.map((account) => (
            <React.Fragment key={account.address}>
              <TableRow hover>
                <TableCell>
                  <IconButton
                    size="small"
                    onClick={() => handleExpandClick(account.address)}
                    aria-label={expanded === account.address ? 'Collapse' : 'Expand'}
                  >
                    {expanded === account.address ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                  </IconButton>
                </TableCell>
                <TableCell>{account.address}</TableCell>
                <TableCell>{account.name}</TableCell>
                <TableCell>{account.owner_id ? ownerDict[account.owner_id]?.name || '' : ''}</TableCell>
                <TableCell>{account.current_balance}</TableCell>
                <TableCell>{String(account.relevant)}</TableCell>
                <TableCell>{account.created_at ? new Date(account.created_at).toLocaleString() : ''}</TableCell>
                <TableCell>
                  <IconButton onClick={() => handleEditClick(account.address)} size="small">
                    <EditIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell colSpan={7} sx={{ p: 0, border: 0 }}>
                  <Collapse in={expanded === account.address} timeout="auto" unmountOnExit>
                    <Box sx={{ m: 2 }}>
                      <Accordion
                        expanded={true}
                        elevation={0}
                        sx={{ boxShadow: 'none', '&:before': { display: 'none' } }}
                      >
                        <AccordionSummary expandIcon={null} sx={{ cursor: 'default' }}>
                          <Typography>Counterparties</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          {counterpartiesLoadingByAccount[account.address] === undefined && !counterpartiesByAccount[account.address] && (
                            (() => {
                              setCounterpartiesLoadingByAccount((prev) => ({ ...prev, [account.address]: true }));
                              getAccountCounterparties(account.address)
                                .then((cps) => {
                                  setCounterpartiesByAccount((prev) => ({ ...prev, [account.address]: cps }));
                                  setCounterpartiesErrorByAccount((prev) => ({ ...prev, [account.address]: null }));
                                })
                                .catch((err) => {
                                  setCounterpartiesErrorByAccount((prev) => ({ ...prev, [account.address]: err.message || 'Failed to load counterparties' }));
                                })
                                .finally(() => {
                                  setCounterpartiesLoadingByAccount((prev) => ({ ...prev, [account.address]: false }));
                                });
                              return null;
                            })()
                          )}
                          {counterpartiesLoadingByAccount[account.address] ? (
                            <CircularProgress size={20} />
                          ) : counterpartiesErrorByAccount[account.address] ? (
                            <Alert severity="error">{counterpartiesErrorByAccount[account.address]}</Alert>
                          ) : counterpartiesByAccount[account.address] && counterpartiesByAccount[account.address].length > 0 ? (
                            <ul style={{ paddingLeft: 16 }}>
                              {counterpartiesByAccount[account.address].map((cp) => (
                                <li key={cp.address}>{cp.address}{cp.name ? ` (${cp.name})` : ''}</li>
                              ))}
                            </ul>
                          ) : (
                            <Typography variant="body2">No counterparties found.</Typography>
                          )}
                          <Box sx={{ mt: 2 }}>
                            {showTransactionsFor === account.address ? (
                              txsLoading[account.address] ? (
                                <CircularProgress size={20} />
                              ) : txsError[account.address] ? (
                                <Alert severity="error">{txsError[account.address]}</Alert>
                              ) : txsByAccount[account.address] && txsByAccount[account.address].length > 0 ? (
                                <>
                                  <Typography variant="subtitle1" gutterBottom>Transactions</Typography>
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell>ID</TableCell>
                                        <TableCell>Source</TableCell>
                                        <TableCell>Target</TableCell>
                                        <TableCell>Amount</TableCell>
                                        <TableCell>Occurred At</TableCell>
                                        <TableCell>Hash</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {txsByAccount[account.address].map((tx) => (
                                        <TableRow key={tx.id}>
                                          <TableCell>{tx.id}</TableCell>
                                          <TableCell>{tx.source_address}</TableCell>
                                          <TableCell>{tx.target_address}</TableCell>
                                          <TableCell>{tx.amount}</TableCell>
                                          <TableCell>{tx.occurred_at ? new Date(tx.occurred_at).toLocaleString() : ''}</TableCell>
                                          <TableCell>{tx.transaction_hash}</TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </>
                              ) : (
                                <Typography variant="body2">No transactions found for this account.</Typography>
                              )
                            ) : (
                              <Button variant="outlined" size="small" onClick={() => {
                                setShowTransactionsFor(account.address);
                                if (!txsByAccount[account.address]) {
                                  setTxsLoading((prev) => ({ ...prev, [account.address]: true }));
                                  getTransactionsForAccount(account.address)
                                    .then((txs) => {
                                      setTxsByAccount((prev) => ({ ...prev, [account.address]: txs }));
                                      setTxsError((prev) => ({ ...prev, [account.address]: null }));
                                    })
                                    .catch((err) => {
                                      setTxsError((prev) => ({ ...prev, [account.address]: err.message || 'Failed to load transactions' }));
                                    })
                                    .finally(() => {
                                      setTxsLoading((prev) => ({ ...prev, [account.address]: false }));
                                    });
                                }
                              }}>Show all transactions</Button>
                            )}
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    </Box>
                  </Collapse>
                </TableCell>
              </TableRow>
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleDrawerClose}
        PaperProps={{ sx: { width: 340, p: 2 } }}
      >
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Edit Account</Typography>
          {drawerLoading ? (
            <CircularProgress />
          ) : drawerError ? (
            <Alert severity="error">{drawerError}</Alert>
          ) : drawerAccount && (
            <>
              <TextField
                label="Address"
                value={drawerAccount.address}
                fullWidth
                margin="normal"
                disabled
              />
              <TextField
                label="Name"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                fullWidth
                margin="normal"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!editRelevant}
                    onChange={(e) => setEditRelevant(e.target.checked)}
                  />
                }
                label="Relevant"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!editIgnored}
                    onChange={(e) => setEditIgnored(e.target.checked)}
                  />
                }
                label="Ignored as Target"
              />
              {editError && <Alert severity="error">{editError}</Alert>}
              <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                <Button onClick={handleDrawerClose} disabled={editLoading}>Cancel</Button>
                <Button
                  variant="contained"
                  onClick={handleEditSave}
                  disabled={editLoading || !editName.trim()}
                >
                  {editLoading ? <CircularProgress size={20} /> : 'Save'}
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Drawer>
    </TableContainer>
  );
};

export default AccountsPage; 